import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';

import { HubMethods } from './model/hub-methods.model';
import { TaskCreateNotification } from './model/task-create-notification.model';
import { TaskItem } from './model/task-item.model';
import { TaskListNotification } from './model/task-list-notification.model';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class TaskStatusService implements OnDestroy {
    readonly message: Observable<{ hubMethod: string; payload: any }>;

    private readonly _messageSubj: Subject<{ hubMethod: string; payload: any }>;

    constructor(private readonly _socketService: SocketService) {
        this._messageSubj = new Subject();
        this.message = this._messageSubj.asObservable();

        this._setupBindings();
    }

    ngOnDestroy(): void {
        this._messageSubj?.complete();
    }

    async create(task: TaskItem): Promise<void> {
        await this._socketService.send(HubMethods.CreateTask, task);
    }

    async update(task: TaskItem): Promise<void> {
        await this._socketService.send(HubMethods.UpdateTask, task);
    }

    async delete(msftTaskId: string): Promise<void> {
        await this._socketService.send(HubMethods.DeleteTask, msftTaskId);
    }

    async list(): Promise<void> {
        await this._socketService.send(HubMethods.ListTasks);
    }

    private _setupBindings(): void {
        if (this._socketService.created) {
            this._bind();
        }
        this._socketService.didCreate
            .pipe(
                tap((_socketService) => {
                    this._bind();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        this._socketService.on(
            HubMethods.OnTaskCreate,
            (payload: TaskCreateNotification): void => {
                this._messageSubj.next({
                    hubMethod: HubMethods.OnTaskCreate,
                    payload,
                });
            },
        );

        this._socketService.on(
            HubMethods.OnTaskList,
            (payload: TaskListNotification): void => {
                this._messageSubj.next({
                    hubMethod: HubMethods.OnTaskList,
                    payload,
                });
            },
        );
    }
}
